import React, { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { PortableText } from '@portabletext/react'
import { client, urlFor } from '../lib/sanityClient'
import { useBlog } from '../context/BlogContext'

const POST_QUERY = `*[_type == "blogPost" && slug.current == $slug && active == true][0] {
  _id,
  title,
  "slug": slug.current,
  excerpt,
  coverImage,
  category,
  author,
  authorRole,
  authorImage,
  publishedAt,
  estimatedReadingTime,
  content,
  seoTitle,
  seoDescription
}`

const CATEGORY_LABELS = {
  'industry-news': 'Industry News',
  'product-updates': 'Product Updates',
  'guides-tips': 'Guides & Tips',
  'case-studies': 'Case Studies',
  'company-news': 'Company News',
  technology: 'Technology',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

const portableComponents = {
  block: {
    normal: ({ children }) => (
      <p className="text-secondary leading-relaxed mb-5 text-base md:text-lg">{children}</p>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-bold text-primary mt-10 mb-4 leading-snug">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-semibold text-primary mt-8 mb-3 leading-snug">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg font-semibold text-primary mt-6 mb-2">{children}</h4>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent pl-5 py-1 my-6 text-secondary italic text-base md:text-lg bg-accent/5 rounded-r-xl pr-4">
        {children}
      </blockquote>
    ),
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold text-primary">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
    code: ({ children }) => (
      <code className="bg-surface-2 text-accent font-mono text-sm px-1.5 py-0.5 rounded">{children}</code>
    ),
    underline: ({ children }) => <span className="underline">{children}</span>,
    link: ({ value, children }) => (
      <a
        href={value.href}
        target={value.blank ? '_blank' : '_self'}
        rel={value.blank ? 'noopener noreferrer' : undefined}
        className="text-accent underline underline-offset-2 hover:text-accent-hover transition-colors"
      >
        {children}
      </a>
    ),
  },
  types: {
    image: ({ value }) => {
      const src = urlFor(value).width(900).auto('format').url()
      return (
        <figure className="my-8">
          <img
            src={src}
            alt={value.alt || ''}
            className="w-full rounded-2xl border border-border-subtle"
          />
          {value.caption && (
            <figcaption className="text-center text-xs text-secondary mt-2">{value.caption}</figcaption>
          )}
        </figure>
      )
    },
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside space-y-1.5 mb-5 text-secondary text-base md:text-lg pl-2">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside space-y-1.5 mb-5 text-secondary text-base md:text-lg pl-2">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
  },
}

export default function BlogPost() {
  const { slug } = useParams()
  const { hasActivePosts, loading: contextLoading } = useBlog()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!slug) return
    setLoading(true)
    setNotFound(false)

    client
      .fetch(POST_QUERY, { slug })
      .then((data) => {
        if (data) {
          setPost(data)
        } else {
          setNotFound(true)
        }
        setLoading(false)
      })
      .catch(() => {
        setNotFound(true)
        setLoading(false)
      })
  }, [slug])

  if (!contextLoading && !hasActivePosts) {
    return <Navigate to="/" replace />
  }

  if (notFound) {
    return <Navigate to="/blog" replace />
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-surface-0 pt-24">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 animate-pulse space-y-6">
          <div className="h-4 bg-surface-2 rounded w-1/4" />
          <div className="h-10 bg-surface-2 rounded w-5/6" />
          <div className="h-10 bg-surface-2 rounded w-3/4" />
          <div className="h-72 bg-surface-2 rounded-2xl" />
          <div className="space-y-3 pt-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`h-4 bg-surface-2 rounded ${i % 3 === 2 ? 'w-3/4' : 'w-full'}`} />
            ))}
          </div>
        </div>
      </main>
    )
  }

  if (!post) return null

  const coverUrl = post.coverImage
    ? urlFor(post.coverImage).width(1200).height(630).auto('format').fit('crop').url()
    : null

  return (
    <main className="min-h-screen bg-surface-0">
      {/* Back nav */}
      <div className="pt-24 pb-0 px-4 md:px-8 lg:px-16">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-secondary hover:text-primary transition-colors duration-200 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5 5-5M6 12h12" />
              </svg>
              All posts
            </Link>
          </motion.div>
        </div>
      </div>

      <article className="px-4 md:px-8 lg:px-16 pt-8 pb-20">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            {/* Category + meta row */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              {post.category && (
                <span className="badge bg-accent/10 text-accent text-xs">
                  {CATEGORY_LABELS[post.category] || post.category}
                </span>
              )}
              <time className="text-xs text-secondary" dateTime={post.publishedAt}>
                {formatDate(post.publishedAt)}
              </time>
              {post.estimatedReadingTime && (
                <>
                  <span className="w-1 h-1 rounded-full bg-border-strong" />
                  <span className="text-xs text-secondary">{post.estimatedReadingTime} min read</span>
                </>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary leading-tight mb-5">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-lg text-secondary leading-relaxed border-l-4 border-accent pl-4">
                {post.excerpt}
              </p>
            )}

            {/* Author */}
            {post.author && (
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border-subtle">
                {post.authorImage ? (
                  <img
                    src={urlFor(post.authorImage).width(48).height(48).fit('crop').url()}
                    alt={post.author}
                    className="w-11 h-11 rounded-full object-cover border border-border-subtle"
                  />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent font-semibold text-base">
                      {post.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-primary">{post.author}</p>
                  {post.authorRole && (
                    <p className="text-xs text-secondary">{post.authorRole}</p>
                  )}
                </div>
              </div>
            )}
          </motion.header>

          {/* Cover image */}
          {coverUrl && (
            <motion.div
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-12"
            >
              <img
                src={coverUrl}
                alt={post.coverImage?.alt || post.title}
                className="w-full rounded-2xl border border-border-subtle object-cover max-h-[480px]"
              />
            </motion.div>
          )}

          {/* Content */}
          {post.content && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="prose-blog"
            >
              <PortableText value={post.content} components={portableComponents} />
            </motion.div>
          )}

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-16 pt-10 border-t border-border-subtle flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div>
              <p className="text-sm font-semibold text-primary">Enjoyed this article?</p>
              <p className="text-sm text-secondary">Explore more insights from the Clutch team.</p>
            </div>
            <Link to="/blog" className="btn-primary text-sm py-2.5 px-5">
              More posts
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </article>
    </main>
  )
}
